import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import hashObject from "object-hash";
import React, { Suspense, useContext, useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import styled from "styled-components";

import {
  Button,
  Loading,
  Toolbar,
  useDebouncedCallback,
} from "@examspark/common-ui";
import { StemDrawing } from "@examspark/types";

import QuestionContext from "../QuestionContext";

import GraphicToken from "./shared/GraphicToken";
import Prompt from "./shared/Prompt";

const Excalidraw = React.lazy(() =>
  import("./common/Excalidraw").then((mod) => ({
    default: mod.Excalidraw,
  })),
);
const MainMenu = React.lazy(() =>
  import("./common/Excalidraw").then((mod) => ({
    default: mod.MainMenu,
  })),
);
const DrawingDropzone = React.lazy(() =>
  import("./common/Excalidraw").then((mod) => ({
    default: mod.DrawingDropzone,
  })),
);

const StyledSuspenseFallback = styled.div`
  padding: 1rem;

  margin: 0 auto;
`;

const CampfireDrawingElementHider = styled.div`
  height: 500px;
  width: 100%;

  .excalidraw {
    .App-toolbar__extra-tools-trigger {
      display: none;
    }

    .SVGLayer {
      display: none;
    }

    .App-menu_top__left .main-menu-trigger,
    .App-toolbar-content .main-menu-trigger {
      // hidden so we don't mess up the flex spacing
      visibility: hidden;
      pointer-events: none;
    }

    // hide the menu in view mode because its empty
    &.excalidraw--view-mode footer.App-toolbar {
      display: none;
    }

    .help-icon {
      display: none;
    }

    .App-menu__left button[aria-label="Add link"] {
      display: none;
    }

    fieldset:has(.FontPicker__container) {
      display: none;
    }

    // unhide me once they add image support to the library
    .layer-ui__wrapper__top-right {
      visibility: hidden;
      pointer-events: none;
    }
  }
`;

const StyledDrawingCanvas = styled.div`
  background-color: #fff;
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  overflow: hidden;
`;

const StyledDrawingCanvasToolbar = styled.div`
  background-color: #f2f2f2;
  border-bottom: solid 1px #ddd;
  padding: 0.7rem;
`;

const StyledDrawingCanvasTokens = styled.div`
  border-top: solid 1px #ddd;

  .item-player-tokens {
    border: none;
    margin: 0;
  }
`;

type Props = {
  data: StemDrawing;
};
const Drawing = (props: Props) => {
  const [excalidrawAPI, setExcalidrawAPI] =
    useState<ExcalidrawImperativeAPI | null>(null);
  const [player, dispatchPlayer] = useContext(QuestionContext);
  const [isInitialized, setInitialized] = useState(false);

  // tokens that are still available to be used
  const filteredTokens = calculateFilteredTokens(props, excalidrawAPI);

  // Excalidraw is very aggressive about calling onChange, so we debounce it to avoid
  // excessive checks
  const lastSeenElementState = React.useRef<string | null>(null);
  const debouncedHandleChange = useDebouncedCallback(
    async (
      elements: readonly ExcalidrawElement[],
      state: AppState,
      files: BinaryFiles,
    ) => {
      const {
        compress,
        serializeAsJSON,
        warnOnInvalidDrawing,
        removeInvalidElements,
      } = await import("./common/Excalidraw");

      const currentElementState = hashObject(elements);

      if (lastSeenElementState.current === currentElementState) {
        return;
      }
      lastSeenElementState.current = currentElementState;

      if (!excalidrawAPI) {
        return;
      }

      if (player.viewOnly) {
        // if we're in view mode, we don't want to save the state
        return;
      }

      // compress the serialized data to save space
      const compressed = await compress(
        serializeAsJSON(
          filterToResponseArea(elements),
          state,
          files,
          "database",
        ),
      );

      // then persist to state
      const blobId = "drawing";
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          blobs: { [blobId]: compressed },
          response: {
            drawing: {
              blobId,
              // currently I'm just using this to bust the autosave cache
              // which is pretty hacky
              checksum: hashObject(compressed),
            },
          },
        },
      });

      warnOnInvalidDrawing(excalidrawAPI, elements, compressed);
      removeInvalidElements(excalidrawAPI, elements);
    },
    150,
  );

  const blob = player.blobs?.[props.data.id]?.["drawing"];
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }

    // if we're in view mode, we're cedeing control to the player
    // blob data entirely
    if (player.viewOnly && isInitialized) {
      const compressed = blob;
      if (!compressed) {
        return;
      }

      // decompress the data
      import("./common/Excalidraw")
        .then((mod) => {
          const { decompress } = mod;
          return decompress(compressed, "gzip");
        })
        .then((decompressed) => {
          if (!decompressed) {
            return;
          }
          const { elements, appState } = JSON.parse(decompressed);
          excalidrawAPI?.updateScene({
            elements,
            appState: {
              ...appState,
              viewModeEnabled: true,
            },
          });
        });
    }
  }, [player.viewOnly, excalidrawAPI, blob, props.data.id, isInitialized]);

  const handleResetDrawing = async () => {
    if (!excalidrawAPI) {
      return;
    }
    if (
      window.confirm("Are you sure you want to reset the drawing?") === false
    ) {
      return;
    }
    // reset the drawing
    const { handleInit } = await import("./common/Excalidraw");
    const initialState = await handleInit(
      excalidrawAPI,
      props.data.content,
      props.data.config,
      !!player.viewOnly,
    );
    excalidrawAPI.updateScene({
      elements: initialState.elements,
      captureUpdate: "IMMEDIATELY",
    });
  };

  return (
    <div className="item-player-element item-player-stem">
      <Prompt data={props.data.content.prompt} />
      <StyledDrawingCanvas>
        <StyledDrawingCanvasToolbar>
          <Toolbar>
            {/* are you sure prompt */}
            <Button title="Reset Drawing" onClick={handleResetDrawing}>
              Reset Drawing
            </Button>
          </Toolbar>
        </StyledDrawingCanvasToolbar>

        {/* @ts-expect-error type is missing children */}
        <DndProvider
          backend={TouchBackend}
          options={{
            enableMouseEvents: true,
            enableHoverOutsideTarget: true,
          }}
          context={window}
        >
          {/* <label>Persisted Size: {drawingBlob?.byteLength} bytes</label> */}
          <CampfireDrawingElementHider>
            <Suspense
              fallback={
                <StyledSuspenseFallback>
                  <Loading />
                </StyledSuspenseFallback>
              }
            >
              <DrawingDropzone
                excalidrawAPI={excalidrawAPI}
                content={props.data.content}
                accept="token"
              >
                <Excalidraw
                  initialData={async () => {
                    const { handleInit } = await import("./common/Excalidraw");
                    const initialState = await handleInit(
                      excalidrawAPI,
                      props.data.content,
                      props.data.config,
                      !!player.viewOnly,
                      blob,
                    );

                    setInitialized(true);

                    return initialState;
                  }}
                  UIOptions={{
                    canvasActions: {
                      changeViewBackgroundColor: false,
                      clearCanvas: false,
                      export: false,
                      loadScene: false,
                      saveToActiveFile: false,
                      toggleTheme: false,
                      saveAsImage: false,
                    },
                    tools: {
                      image: false,
                    },
                    dockedSidebarBreakpoint: undefined,
                  }}
                  aiEnabled={false}
                  excalidrawAPI={setExcalidrawAPI}
                  isCollaborating={false}
                  onChange={debouncedHandleChange}
                  renderCustomStats={() => <></>}
                  renderEmbeddable={() => <></>}
                  renderTopRightUI={() => <></>}
                >
                  {/* this empties the default main menu. Above, we hide the button itself */}
                  <MainMenu />
                </Excalidraw>
              </DrawingDropzone>
            </Suspense>
          </CampfireDrawingElementHider>
          {props.data.content.tokens && (
            <StyledDrawingCanvasTokens>
              <div className="item-player-tokens">
                {filteredTokens.map((token) => (
                  <GraphicToken
                    key={token.id}
                    data={token}
                    useDragType="token"
                  />
                ))}
              </div>
            </StyledDrawingCanvasTokens>
          )}
        </DndProvider>
      </StyledDrawingCanvas>
    </div>
  );
};

const filterToResponseArea = (elements: readonly ExcalidrawElement[]) => {
  const responseAreaFrame = elements.find(
    (el) => el.customData?.internalId === "response-area",
  );
  if (!responseAreaFrame) {
    return elements; // no response area? return all elements
  }
  const elementsInResponseArea = elements
    .filter(
      (el) =>
        el.frameId === responseAreaFrame.id || el.id === responseAreaFrame.id,
    )
    .filter((el) => el.isDeleted !== true);

  return elementsInResponseArea;
};

const calculateFilteredTokens = (
  props: Props,
  excalidrawAPI: ExcalidrawImperativeAPI | null,
) => {
  const elements = excalidrawAPI?.getSceneElements() ?? [];
  const responseAreaFrame = elements.find(
    (el) => el.customData?.internalId === "response-area",
  );

  if (!responseAreaFrame || !excalidrawAPI) {
    return props.data.content.tokens || [];
  }

  // filter out tokens that are already in use, above their maxUsage
  const filteredTokens = [];

  for (const token of props.data.content.tokens || []) {
    const matchingElement = filterToResponseArea(elements).find(
      (el) => el.customData?.tokenId === token.id,
    );

    if (!matchingElement) {
      filteredTokens.push(token);
      continue;
    }

    const usageCount = elements.filter(
      (el) => el.customData?.tokenId === token.id,
    ).length;

    if (!token.maxUsage || usageCount < token.maxUsage) {
      filteredTokens.push(token);
    }
  }

  return filteredTokens;
};

export default Drawing;
